import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMonitorStats, updateMonitorStat, exportMonitors } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Users, Edit2, Download, Search, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { createPDF, addTable } from '../lib/pdf';

export default function MonitorsPage() {
  const { courseCategories } = useAuth();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', total_courses: 0, total_earnings: 0, total_days: 0 });
  const [search, setSearch] = useState('');
  const [calendarDate, setCalendarDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await getMonitorStats(params);
      setStats(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEdit = (s) => { setEditForm({ name: s.name, total_courses: s.total_courses, total_earnings: s.total_earnings, total_days: s.total_days }); setEditDialog(true); };
  const handleSaveEdit = async () => {
    try {
      await updateMonitorStat(editForm.name, { total_courses: editForm.total_courses, total_earnings: editForm.total_earnings, total_days: editForm.total_days });
      toast.success('Actualizado');
      setEditDialog(false);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleExportExcel = async () => { try { const { data } = await exportMonitors(); const url = URL.createObjectURL(data); const a = document.createElement('a'); a.href = url; a.download = 'monitores.xlsx'; a.click(); } catch { toast.error('Error'); } };
  const handleExportPDF = () => {
    const doc = createPDF(); doc.setFontSize(16); doc.text('Monitores - SURF SKULL', 14, 18);
    const allCats = Array.from(new Set(stats.flatMap(s => Object.keys(s.courses_by_category || {}))));
    const head = [['Monitor', ...allCats.map(c => c.charAt(0).toUpperCase() + c.slice(1)), 'Días', 'Ganancias']];
    const body = stats.map(s => [s.name, ...allCats.map(c => (s.courses_by_category || {})[c] || 0), s.total_days, `${(s.total_earnings || 0).toFixed(2)} €`]);
    addTable(doc, { startY: 25, head, body, theme: 'grid', headStyles: { fillColor: [74, 109, 124] } });
    doc.save('monitores.pdf');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="monitors-page">
      <div className="sticky top-0 z-10 bg-[#FCFBF9] pt-4 md:pt-8 pb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Monitores</h1>
          <p className="text-sm text-[#8A9BA8] mt-1">Datos automáticos de Cursos · Editable en caso de error</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportExcel} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 mr-1"/>Excel</Button>
          <Button variant="outline" onClick={handleExportPDF} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 mr-1"/>PDF</Button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-end">
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Desde</Label><Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setCalendarDate(null); }} className="border-[#E5E0D8] w-44"/></div>
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Hasta</Label><Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setCalendarDate(null); }} className="border-[#E5E0D8] w-44"/></div>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild><Button variant="outline" size="icon" className={`border-[#E5E0D8] h-9 w-9 ${calendarDate ? 'bg-[#4A6D7C] text-white hover:bg-[#3A5663]' : 'text-[#4A5D6C]'}`}><CalendarDays className="w-4 h-4"/></Button></PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={calendarDate} month={calendarMonth} onMonthChange={setCalendarMonth} onSelect={(d) => { if (d) { const ds = d.toISOString().split('T')[0]; setDateFrom(ds); setDateTo(ds); setCalendarDate(d); setCalendarMonth(d); } setCalendarOpen(false); }} /></PopoverContent>
        </Popover>
        {calendarDate && <Button variant="ghost" size="sm" className="h-7 text-xs text-[#8A9BA8]" onClick={() => { setCalendarDate(null); setDateFrom(''); setDateTo(''); setCalendarMonth(new Date()); }}>✕ {calendarDate.toLocaleDateString('es-ES')}</Button>}
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#8A9BA8]"/><Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 border-[#E5E0D8] w-24 sm:w-24 sm:w-44 h-9"/></div>
      </div>

      {loading ? <div className="skeleton-pulse h-40 rounded-lg"/> : (() => {
        const filteredStats = stats.filter(s => { if (!search) return true; const q = search.toLowerCase(); return [s.name, s.total_courses?.toString(), s.total_earnings?.toString()].some(f => f && f.toLowerCase().includes(q)); }).sort((a, b) => (b.total_earnings || 0) - (a.total_earnings || 0));
        const activeCats = Array.from(new Set(filteredStats.flatMap(s => Object.keys(s.courses_by_category || {}))));
        return filteredStats.length === 0 ? (
        <div className="text-center py-16 text-[#8A9BA8]"><Users className="w-10 h-10 mx-auto mb-3 opacity-40"/>No hay datos de monitores</div>
      ) : (
        <div className="border border-[#E5E0D8] rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5F2ED]">
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Monitor</TableHead>
                {activeCats.map(cat => (
                  <TableHead key={cat} className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8] text-right capitalize">{cat}</TableHead>
                ))}
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8] text-right">Días</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8] text-right">Ganancias</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStats.map((s, i) => (
                <TableRow key={i} data-testid={`monitor-stat-${i}`}>
                  <TableCell className="text-sm font-medium text-[#1A2B3C]">{s.name}</TableCell>
                  {activeCats.map(cat => (
                    <TableCell key={cat} className="text-sm text-right tabular-nums">{(s.courses_by_category || {})[cat] || 0}</TableCell>
                  ))}
                  <TableCell className="text-sm text-right tabular-nums">{s.total_days}</TableCell>
                  <TableCell className="text-sm text-right font-semibold text-[#81B29A] tabular-nums">{(s.total_earnings || 0).toFixed(2)} €</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => handleEdit(s)} className="h-7 w-7 text-[#4A6D7C]"><Edit2 className="w-3.5 h-3.5"/></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
      })()}

      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-[#FCFBF9] border-[#E5E0D8] w-[92vw] sm:w-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Editar: {editForm.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Cursos</Label><Input type="number" value={editForm.total_courses} onChange={e => setEditForm({...editForm, total_courses: parseInt(e.target.value)||0})} className="border-[#E5E0D8]"/></div>
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Días</Label><Input type="number" value={editForm.total_days} onChange={e => setEditForm({...editForm, total_days: parseInt(e.target.value)||0})} className="border-[#E5E0D8]"/></div>
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Ganancias (€)</Label><Input type="number" step="0.01" value={editForm.total_earnings} onChange={e => setEditForm({...editForm, total_earnings: parseFloat(e.target.value)||0})} className="border-[#E5E0D8]"/></div>
            <Button onClick={handleSaveEdit} className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
